import { useState, useEffect } from "react";
import moment, { Moment } from "moment";
import {
  TextField,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

export interface DateTimePickerProps {
  value?: Moment | null;
  onChange?: (value: Moment | null) => void;
  label?: string;
  fullWidth?: boolean;
  invoiceDate?: string;
}

const DateTimePicker = ({
  value: propValue = null,
  onChange,
  label = "Select date & time",
  fullWidth = true,
  invoiceDate,
}: DateTimePickerProps): JSX.Element => {
  const [value, setValue] = useState<Moment | null>(propValue);
  const [error, setError] = useState<string>("");

  // Sync internal state with prop
  useEffect(() => {
    setValue(propValue ?? null);
  }, [propValue]);

  const maxDateTime = moment().endOf("day");
  const minDateTime = invoiceDate ? moment(invoiceDate) : undefined;

  const handleChange = (newVal: Moment | null) => {
    setError("");

    if (!newVal) {
      setValue(null);
      onChange?.(null);
      return;
    }

    const m = moment(newVal);

    if (minDateTime && m.isBefore(minDateTime)) {
      setError("Date/time cannot be before invoice date.");
      return;
    }

    if (m.isAfter(maxDateTime)) {
      setError("Future date/time is not allowed.");
      return;
    }

    setValue(m);
    onChange?.(m);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing={0.5}>
        <MobileDateTimePicker
          label={label}
          value={value}
          onChange={handleChange}
          views={["year", "month", "day", "hours", "minutes"]}
          format="YYYY-MM-DD HH:mm"
          ampm={false}
          minutesStep={1}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          slots={{
            textField: (params: any) => {
              const originalEndAdornment =
                params?.InputProps?.endAdornment ?? null;

              return (
                <TextField
                  {...params}
                  fullWidth={fullWidth}
                  size="small"
                  error={Boolean(error) || Boolean(params?.error)}
                  helperText={error || params?.helperText}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {originalEndAdornment}
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="calendar"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <CalendarTodayIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      </>
                    ),
                  }}
                />
              );
            },
          }}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default DateTimePicker;